import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { debounce } from "lodash";

import { Grid, Button, CircularProgress, Box } from "@material-ui/core";

import AutoCompleteField from "components/_refactor/fields/autocomplete";
import InputText from "components/_refactor/text/input";

import themeColorsDesingSystem from "assets/theme/ds-colors";

import {
  loadLinhaVendaOptions,
  loadGradeOptions,
} from "../../../shared/utils/grade-filter-options";

export default function ModalConsultFilters({
  values,
  onChange,
  onSearch,
  searching,
  idMarca,
}) {
  const [linhasVenda, setLinhasVenda] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loadingFilters, setLoadingFilters] = useState({
    linhasVenda: false,
    grades: false,
  });

  const linhaVendaLoadSeqRef = useRef(0);
  const gradeLoadSeqRef = useRef(0);
  const idMarcaRef = useRef(idMarca);
  idMarcaRef.current = idMarca;

  const selectedLinhaVenda = values.linhaVenda?.label ?? "";

  const handleFieldChange = (key, value) => {
    const next = { ...values, [key]: value };
    if (key === "linhaVenda") {
      next.grade = null;
    }
    onChange(next);
  };

  const handleGetLinhasVenda = useCallback(async (searchTerm = "") => {
    if (!idMarcaRef.current) {
      setLinhasVenda([]);
      return;
    }

    const seq = ++linhaVendaLoadSeqRef.current;
    setLoadingFilters((prev) => ({ ...prev, linhasVenda: true }));

    try {
      const options = await loadLinhaVendaOptions({
        idMarca: idMarcaRef.current,
        filter: searchTerm,
      });

      if (seq !== linhaVendaLoadSeqRef.current) return;
      setLinhasVenda(options);
    } catch (error) {
      if (seq !== linhaVendaLoadSeqRef.current) return;
      console.error("Erro ao buscar linhas de venda", error);
      setLinhasVenda([]);
    } finally {
      if (seq === linhaVendaLoadSeqRef.current) {
        setLoadingFilters((prev) => ({ ...prev, linhasVenda: false }));
      }
    }
  }, []);

  const handleGetGrades = useCallback(
    async (searchTerm = "") => {
      if (!idMarcaRef.current) {
        setGrades([]);
        return;
      }

      const seq = ++gradeLoadSeqRef.current;
      setLoadingFilters((prev) => ({ ...prev, grades: true }));
      try {
        const options = await loadGradeOptions({
          idMarca: idMarcaRef.current,
          linhaVenda: selectedLinhaVenda,
          filter: searchTerm,
        });

        if (seq !== gradeLoadSeqRef.current) return;
        setGrades(options);
      } catch (error) {
        if (seq !== gradeLoadSeqRef.current) return;
        console.error("Erro ao buscar grades", error);
        setGrades([]);
      } finally {
        if (seq === gradeLoadSeqRef.current) {
          setLoadingFilters((prev) => ({ ...prev, grades: false }));
        }
      }
    },
    [selectedLinhaVenda],
  );

  const debouncedGetLinhasVenda = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetLinhasVenda(searchTerm);
      }, 650),
    [handleGetLinhasVenda],
  );

  const handleLinhaVendaInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetLinhasVenda(inputValue ?? "");
    },
    [debouncedGetLinhasVenda],
  );

  const debouncedGetGrades = useMemo(
    () =>
      debounce((searchTerm) => {
        handleGetGrades(searchTerm);
      }, 650),
    [handleGetGrades],
  );

  const handleGradeInputChange = useCallback(
    (_, inputValue, reason) => {
      if (reason !== "input") return;
      debouncedGetGrades(inputValue ?? "");
    },
    [debouncedGetGrades],
  );

  useEffect(() => {
    handleGetGrades();
  }, [idMarca, handleGetGrades]);

  useEffect(() => {
    handleGetLinhasVenda("");
  }, [idMarca, handleGetLinhasVenda]);

  useEffect(() => {
    return () => {
      debouncedGetLinhasVenda.cancel();
      debouncedGetGrades.cancel();
    };
  }, [debouncedGetLinhasVenda, debouncedGetGrades]);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        onSearch();
      }}
    >
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={12} sm={6} md={3}>
          <InputText
            label="Pesquisar produto"
            placeholder="Buscar..."
            value={values.productSearch ?? ""}
            onChange={(e) => handleFieldChange("productSearch", e.target.value)}
            disabled={searching}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <AutoCompleteField
            options={linhasVenda}
            label="Linha"
            optionLabelKey="label"
            optionIdKey="id"
            onChange={(option) => handleFieldChange("linhaVenda", option)}
            onInputChange={handleLinhaVendaInputChange}
            value={values.linhaVenda}
            disabled={searching}
            loading={loadingFilters.linhasVenda}
            preserveInputWhenLoading
            disableClientFilter
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <AutoCompleteField
            options={grades}
            label="Grade"
            optionLabelKey="label"
            optionIdKey="id"
            onChange={(option) => handleFieldChange("grade", option)}
            onInputChange={handleGradeInputChange}
            value={values.grade}
            disabled={searching}
            loading={loadingFilters.grades}
            preserveInputWhenLoading
            disableClientFilter
          />
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={searching}
              style={{
                minWidth: 160,
                height: 40,
                backgroundColor: themeColorsDesingSystem.primary.main,
              }}
            >
              {searching ? (
                <CircularProgress size={20} style={{ color: "white" }} />
              ) : (
                "Consultar"
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
